import { Content, Header, Page } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { Box, Card, CardContent, Typography } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import {
  useControlPlaneOnboarding,
  type ControlPlaneOnboardingView,
} from '../hooks/useControlPlaneOnboarding';
import {
  useControlPlaneManagement,
  type ControlPlaneManagementView,
} from '../hooks/useControlPlaneManagement';
import { activityCockpitRouteRef } from '../routes';
import {
  ConnectionSection,
  canonicalAnnouncement,
} from './ControlPlaneConnectionSection';
import {
  ManagementStatePanel,
  ManagementTechnicalDetails,
} from './ControlPlaneManagementPanels';
import { ManagementActions } from './ControlPlaneManagementActions';
import type { ManagementActionProps } from './ControlPlaneActionGroups';
import { useConnectionPageStyles } from './useConnectionPageStyles';

function authorizedTitle(
  activityId: string,
  view: ControlPlaneOnboardingView,
  management: ControlPlaneManagementView,
) {
  // An ACL-scoped reader may see the management title without owner access.
  let activity = view.activity?.id === activityId ? view.activity : undefined;
  if (!activity && management.activity?.id === activityId)
    activity = management.activity;
  return activity?.title.trim();
}

export function ActivityConnectionPage({ activityId }: { activityId: string }) {
  const classes = useConnectionPageStyles();
  const view = useControlPlaneOnboarding(activityId);
  const management = useControlPlaneManagement(activityId);
  const navigate = useNavigate();
  const activityCockpitRoute = useRouteRef(activityCockpitRouteRef);
  const title = authorizedTitle(activityId, view, management);
  // Navigation stays explicit; a stored handoff never redirects this page.
  return (
    <Page themeId="tool">
      <Header
        title={title || 'Control Plane'}
        subtitle="Gerencie a conexão desta atividade"
      />
      <Content>
        <Box className={classes.layout}>
          <ProjectionReadFailure view={view} management={management} />
          <ConnectionContent
            management={management}
            onboarding={view}
            activityTitle={title || 'Atividade'}
            openCockpit={() => navigate(activityCockpitRoute({ activityId }))}
          />
        </Box>
      </Content>
    </Page>
  );
}

function ProjectionReadFailure({
  view,
  management,
}: {
  view: ControlPlaneOnboardingView;
  management: ControlPlaneManagementView;
}) {
  const classes = useConnectionPageStyles();
  const unavailable =
    !management.loading && !management.projection && management.error !== null;
  // Otherwise ConnectionSection already owns the single error alert.
  if (!unavailable || !view.activity) return null;
  return (
    <Card variant="outlined">
      <CardContent className={classes.section}>
        <Typography role="alert" color="error">
          {management.error ??
            'Não foi possível ler o estado operacional desta atividade.'}
        </Typography>
        <Typography variant="body2">
          Nenhum estado é apresentado enquanto a leitura canônica estiver
          indisponível. Recarregue para tentar novamente.
        </Typography>
      </CardContent>
    </Card>
  );
}

function ConnectionContent(props: ManagementActionProps) {
  const classes = useConnectionPageStyles();
  const { onboarding: view, management } = props;
  const showConnection =
    Boolean(view.activity) || view.loading || !management.projection;
  return (
    <Card variant="outlined">
      <CardContent className={classes.section}>
        {management.projection ? (
          <ManagementStatePanel
            projection={management.projection}
            stale={management.stale}
          />
        ) : null}
        {showConnection ? (
          <ConnectionSection
            view={view}
            pairCapability={management.projection?.capabilities.pair}
            announcement={
              view.announcement || canonicalAnnouncement(management.projection)
            }
          />
        ) : null}
        {management.projection ? <ManagementContent {...props} /> : null}
      </CardContent>
    </Card>
  );
}

function ManagementContent(props: ManagementActionProps) {
  const classes = useConnectionPageStyles();
  const { management } = props;
  if (!management.projection) return null;
  return (
    <>
      <ManagementActions {...props} />
      {management.error ? (
        <Typography role="alert" color="error">
          {management.error}
        </Typography>
      ) : null}
      {/* The pairing section owns role="status"; keep this announcement separate. */}
      <Typography
        aria-live="polite"
        aria-atomic="true"
        aria-label="Estado da gestão da atividade"
        variant="body2"
        className={classes.liveRegion}
      >
        {management.announcement}
      </Typography>
      <ManagementTechnicalDetails
        projection={management.projection}
        stale={management.stale}
      />
    </>
  );
}
